import { apiSlice } from "../../../features/api/apiSlice";

/**
 * Secretariat mail-out.
 *
 * A campaign is a draft — editable, attachable, testable — until it is sent.
 * Sending resolves the chosen groups into one recipient row per address and
 * queues a job for each, so the delivery report is per-address, not per-blast.
 */

// ── Types ────────────────────────────────────────────────────────────────────

export type CampaignStatus = "draft" | "sending" | "sent";

/**
 * Which sender a campaign goes out through.
 *
 *   brevo  — the relay. Built for volume, but a shared sending domain, which is
 *            part of why a circular can be filed under Promotions.
 *   cpanel — the Secretariat's own webmail. Reads as ordinary correspondence;
 *            the host caps how many go out per hour, so it suits smaller sends.
 */
export type CampaignMailer = "cpanel" | "brevo";

/**
 * How the message leaves the building.
 *
 * The audience, the delivery report and the retry step are the same on both —
 * only the address and the transport differ, which is why this is one module
 * with a switch rather than two screens.
 */
export type CampaignChannel = "email" | "sms";

export const DEFAULT_CHANNEL: CampaignChannel = "email";

/** The networks cut a text here, and the API validates against the same figure. */
export const SMS_MAX_LENGTH = 1000;

/**
 * What a message leaves by unless the composer says otherwise — the Institute's
 * own mailbox, so a Secretariat notice arrives looking like one. Kept in step
 * with MailCampaign::DEFAULT_MAILER on the API.
 */
export const DEFAULT_MAILER: CampaignMailer = "cpanel";

/**
 * The GROUPS chosen, not the addresses they resolve to. Resolution happens on
 * the server at send time, so a draft goes to the membership as it stands when
 * it is actually sent.
 */
export type Audience = {
  all_members?: boolean;
  chapters?: string[];
  categories?: string[];
  states?: string[];
  committees?: string[];
  upgrade_diets?: string[];
  /** Candidates who have PAID for these exam sittings. */
  exam_diets?: string[];
  /** Everyone registered for these events — members and guests alike. */
  events?: string[];
  /** true = whoever is in office now; an array names executive sets. */
  executives?: boolean | string[];
  /** true = every chapter; an array names chapters. */
  chapter_chairmen?: boolean | string[];
  /**
   * The National Policy Committee — past presidents and secretaries, the
   * trustees, the national body chairmen, the Institute's outside
   * representatives. true = everyone serving on it; an array names seats.
   * Only CURRENT holders are ever resolved; past ones are on the register for
   * the record, not to be written to as though they still held the office.
   */
  national_positions?: boolean | string[];
  member_ids?: string[];
  emails?: string[];

  /* Refinements. These NARROW the groups above rather than adding to them, and
     are not an audience on their own — see REFINEMENT_KEYS below. */

  /** "owing" | "up_to_date" — financial standing on dues. */
  dues?: string;
  /** Registration date range, YYYY-MM-DD. Either end stands alone. */
  reg_from?: string;
  reg_to?: string;
};

/**
 * The keys that filter an audience rather than form one.
 *
 * Kept separate because "Send" is enabled by having somebody to send TO: a
 * dues filter with no group ticked describes nobody, and must not read as a
 * selection.
 */
export const REFINEMENT_KEYS = ["dues", "reg_from", "reg_to"] as const;

export type CampaignAttachment = {
  id: string;
  filename: string;
  path: string;
  mime_type?: string | null;
  size: number;
  url: string;
};

export type MailCampaign = {
  id: string;
  subject: string;
  body: string;
  audience: Audience | null;
  /** false when the message carries its own greeting, e.g. "Dear Esteemed Member,". */
  personalise_greeting: boolean;
  mailer: CampaignMailer;
  channel?: CampaignChannel;
  status: CampaignStatus;
  recipients_count: number;
  sent_count: number;
  failed_count: number;
  queued_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  attachments?: CampaignAttachment[];
  attachments_count?: number;
  creator?: { id: number; name: string } | null;
};

export type GroupOption = { id: string; name: string; is_active?: boolean; status?: string };

export type GroupOptions = {
  chapters: GroupOption[];
  categories: GroupOption[];
  states: GroupOption[];
  committees: GroupOption[];
  upgrade_diets: GroupOption[];
  exam_diets: GroupOption[];
  events: GroupOption[];
  executive_sets: GroupOption[];
  /** Seats on the National Policy Committee, each labelled with its section
   *  and how many people are serving in it. */
  national_positions: GroupOption[];
  active_executive_set_id: string | null;
};

export type AudienceSummary = {
  total: number;
  /** Matched, but not mailable — placeholder addresses and blanks. */
  skipped: number;
  skipped_reasons: Record<string, number>;
  by_group: Record<string, number>;
  sample: { name: string | null; email: string; source: string }[];
};

export type CampaignRecipient = {
  id: string;
  name: string | null;
  email: string;
  source: string | null;
  status: "pending" | "sent" | "failed";
  error_message: string | null;
  sent_at: string | null;
};

export type Paginated<T> = {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Files force multipart, and multipart cannot carry a nested object — so the
 * audience travels as a JSON string, which the controller decodes either way.
 */
function composeBody(
  fields: {
    subject: string;
    body: string;
    audience: Audience;
    personalise_greeting: boolean;
    mailer: CampaignMailer;
  channel?: CampaignChannel;
  },
  files?: File[],
): FormData | Record<string, unknown> {
  if (!files?.length) return { ...fields };

  const fd = new FormData();
  fd.append("subject", fields.subject);
  fd.append("body", fields.body);
  fd.append("audience", JSON.stringify(fields.audience));
  // PHP reads the string "false" as true, so booleans travel as "1"/"0".
  fd.append("personalise_greeting", fields.personalise_greeting ? "1" : "0");
  fd.append("mailer", fields.mailer);
  files.forEach((f) => fd.append("attachments[]", f));
  return fd;
}

/** Every group that resolves to nobody is dropped, so an empty key never posts. */
export function pruneAudience(audience: Audience): Audience {
  const out: Audience = {};
  if (audience.all_members) out.all_members = true;
  if (audience.executives) out.executives = audience.executives;
  if (audience.chapter_chairmen) out.chapter_chairmen = audience.chapter_chairmen;
  if (audience.national_positions === true) out.national_positions = true;
  else if (Array.isArray(audience.national_positions) && audience.national_positions.length) {
    out.national_positions = audience.national_positions;
  }

  (["chapters", "categories", "states", "committees", "upgrade_diets", "exam_diets", "events", "member_ids", "emails"] as const)
    .forEach((key) => {
      const list = audience[key];
      if (Array.isArray(list) && list.length) out[key] = list;
    });

  REFINEMENT_KEYS.forEach((key) => {
    const v = audience[key];
    if (typeof v === "string" && v.trim()) out[key] = v.trim();
  });

  return out;
}

/** No GROUP selected — a refinement on its own describes nobody to send to. */
export function audienceIsEmpty(audience: Audience): boolean {
  return Object.keys(pruneAudience(audience))
    .filter((k) => !(REFINEMENT_KEYS as readonly string[]).includes(k))
    .length === 0;
}

// ── Endpoints ────────────────────────────────────────────────────────────────

export const mailApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMailCampaigns: builder.query<
      Paginated<MailCampaign>,
      { status?: string; search?: string; page?: number } | void
    >({
      query: (arg) => {
        const q = new URLSearchParams();
        if (arg && "status" in arg && arg.status) q.set("status", arg.status);
        if (arg && "search" in arg && arg.search) q.set("search", arg.search);
        if (arg && "page" in arg && arg.page) q.set("page", String(arg.page));
        const qs = q.toString();
        return `/admin/mail-campaigns${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["MailCampaigns"],
    }),

    getMailCampaign: builder.query<
      { campaign: MailCampaign; audience: AudienceSummary | null },
      string
    >({
      query: (id) => `/admin/mail-campaigns/${id}`,
      providesTags: (_r, _e, id) => [{ type: "MailCampaign" as const, id }],
    }),

    getMailGroups: builder.query<GroupOptions, void>({
      query: () => "/admin/mail-campaigns/meta/groups",
      providesTags: ["MailGroups"],
    }),

    /** Live head-count while the groups are being picked. */
    /**
     * The head-count differs by channel — a member with an address and no
     * mobile is in one audience and not the other — so the preview is asked
     * for the channel the composer is on.
     */
    previewAudience: builder.mutation<
      AudienceSummary,
      Audience | { audience: Audience; channel?: CampaignChannel }
    >({
      query: (arg: any) => ({
        url: "/admin/mail-campaigns/audience/preview",
        method: "POST",
        body:
          arg && typeof arg === "object" && "audience" in arg
            ? { audience: arg.audience, channel: arg.channel }
            : { audience: arg },
      }),
    }),

    createMailCampaign: builder.mutation<
      { message: string; campaign: MailCampaign },
      {
        subject: string;
        body: string;
        audience: Audience;
        personalise_greeting: boolean;
        mailer: CampaignMailer;
  channel?: CampaignChannel;
        files?: File[];
      }
    >({
      query: ({ files, ...fields }) => ({
        url: "/admin/mail-campaigns",
        method: "POST",
        body: composeBody(fields, files),
      }),
      invalidatesTags: ["MailCampaigns"],
    }),

    updateMailCampaign: builder.mutation<
      { message: string; campaign: MailCampaign },
      {
        id: string;
        subject: string;
        body: string;
        audience: Audience;
        personalise_greeting: boolean;
        mailer: CampaignMailer;
  channel?: CampaignChannel;
        files?: File[];
      }
    >({
      query: ({ id, files, ...fields }) => ({
        url: `/admin/mail-campaigns/${id}`,
        method: "POST",
        body: composeBody(fields, files),
      }),
      invalidatesTags: (_r, _e, { id }) => ["MailCampaigns", { type: "MailCampaign" as const, id }],
    }),

    deleteMailCampaign: builder.mutation<{ message: string }, string>({
      query: (id) => ({ url: `/admin/mail-campaigns/${id}`, method: "DELETE" }),
      invalidatesTags: ["MailCampaigns"],
    }),

    deleteCampaignAttachment: builder.mutation<
      { message: string },
      { id: string; attachmentId: string }
    >({
      query: ({ id, attachmentId }) => ({
        url: `/admin/mail-campaigns/${id}/attachments/${attachmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "MailCampaign" as const, id }],
    }),

    testMailCampaign: builder.mutation<{ message: string }, { id: string; email?: string }>({
      query: ({ id, email }) => ({
        url: `/admin/mail-campaigns/${id}/test`,
        method: "POST",
        body: email ? { email } : {},
      }),
    }),

    sendMailCampaign: builder.mutation<
      { message: string; recipients: number; campaign: MailCampaign },
      string
    >({
      query: (id) => ({ url: `/admin/mail-campaigns/${id}/send`, method: "POST" }),
      invalidatesTags: (_r, _e, id) => ["MailCampaigns", { type: "MailCampaign" as const, id }],
    }),

    getCampaignRecipients: builder.query<
      { recipients: Paginated<CampaignRecipient>; counts: Record<string, number> },
      { id: string; status?: string; search?: string; page?: number }
    >({
      query: ({ id, status, search, page }) => {
        const q = new URLSearchParams();
        if (status) q.set("status", status);
        if (search) q.set("search", search);
        if (page) q.set("page", String(page));
        const qs = q.toString();
        return `/admin/mail-campaigns/${id}/recipients${qs ? `?${qs}` : ""}`;
      },
      providesTags: (_r, _e, { id }) => [{ type: "MailCampaignRecipients" as const, id }],
    }),

    retryFailedRecipients: builder.mutation<{ message: string; requeued: number }, string>({
      query: (id) => ({ url: `/admin/mail-campaigns/${id}/retry-failed`, method: "POST" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "MailCampaign" as const, id },
        { type: "MailCampaignRecipients" as const, id },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMailCampaignsQuery,
  useGetMailCampaignQuery,
  useGetMailGroupsQuery,
  usePreviewAudienceMutation,
  useCreateMailCampaignMutation,
  useUpdateMailCampaignMutation,
  useDeleteMailCampaignMutation,
  useDeleteCampaignAttachmentMutation,
  useTestMailCampaignMutation,
  useSendMailCampaignMutation,
  useGetCampaignRecipientsQuery,
  useRetryFailedRecipientsMutation,
} = mailApi;
